import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OAuthProviderCredentialsSummary } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { platformApi } from '@/services/platform.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Switch } from '@/components/ui';
import { OAUTH_ICONS } from '@/components/oauth-icons';

const QUERY_KEY = ['platform', 'oauth-providers'];

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

function ProviderCard({ summary }: { summary: OAuthProviderCredentialsSummary }): ReactNode {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const isApple = summary.provider === 'apple';
  const isConfigured = Boolean(summary.clientId);

  const [expanded, setExpanded] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState('');
  const [isChangingSecret, setIsChangingSecret] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [keyId, setKeyId] = useState<string | null>(null);
  const [privateKey, setPrivateKey] = useState('');
  const [isChangingPrivateKey, setIsChangingPrivateKey] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const showSecretInput = isChangingSecret || !summary.hasClientSecret;
  const showPrivateKeyInput = isChangingPrivateKey || !summary.hasPrivateKey;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const save = useMutation({
    mutationFn: () =>
      platformApi.updateOAuthProviderCredentials(summary.provider, {
        enabled: summary.enabled,
        clientId: clientId ?? summary.clientId ?? '',
        clientSecret: !isApple && showSecretInput && clientSecret ? clientSecret : undefined,
        teamId: isApple ? (teamId ?? summary.teamId ?? undefined) : undefined,
        keyId: isApple ? (keyId ?? summary.keyId ?? undefined) : undefined,
        privateKey: isApple && showPrivateKeyInput && privateKey ? privateKey : undefined,
      }),
    onSuccess: async () => {
      setSaveError(null);
      // Whatever earlier stopped the switch from turning on — most likely
      // "save credentials first" — no longer applies once a save succeeds.
      setToggleError(null);
      setIsChangingSecret(false);
      setClientSecret('');
      setIsChangingPrivateKey(false);
      setPrivateKey('');
      await invalidate();
    },
    onError: (error: unknown) => setSaveError(errorMessage(error, t)),
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      platformApi.updateOAuthProviderCredentials(summary.provider, {
        enabled,
        clientId: summary.clientId ?? '',
      }),
    onSuccess: async () => {
      setToggleError(null);
      await invalidate();
    },
    onError: (error: unknown) => setToggleError(errorMessage(error, t)),
  });

  function handleToggle(next: boolean): void {
    if (next && !isConfigured) {
      // Nothing saved yet — flipping the switch would send an incomplete
      // row the server can only reject. Open the form instead of firing a
      // request that is certain to fail.
      setExpanded(true);
      setToggleError(t('platform.oauth.needsCredentialsFirst'));
      return;
    }
    toggle.mutate(next);
  }

  return (
    <div className="settings-card oauth-provider-card">
      <div className="oauth-provider-header">
        <button
          type="button"
          className="oauth-provider-summary"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {OAUTH_ICONS[summary.provider]()}
          <span className="oauth-provider-name">
            {t(`auth.oauth.continueWith.${summary.provider}`)}
          </span>
          <span className="badge" data-status={summary.active ? 'ACTIVE' : undefined}>
            {t(
              summary.active
                ? `platform.oauth.sourceBadge.${summary.source}`
                : 'platform.oauth.sourceBadge.none',
            )}
          </span>
        </button>

        <Switch
          checked={summary.enabled}
          onChange={handleToggle}
          label={t('platform.oauth.enableProvider', {
            values: { provider: t(`auth.oauth.continueWith.${summary.provider}`) },
          })}
          labelHidden
          disabled={toggle.isPending}
        />
      </div>

      {toggleError && <Alert tone="danger">{toggleError}</Alert>}

      {!expanded && (
        <button type="button" className="settings-inline-link" onClick={() => setExpanded(true)}>
          {isConfigured ? t('platform.oauth.editCredentials') : t('platform.oauth.addCredentials')}
        </button>
      )}

      {expanded && (
        <form
          className="oauth-provider-form"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div className="form-grid">
            <Field
              label={t('platform.oauth.clientId')}
              name={`${summary.provider}-client-id`}
              value={clientId ?? summary.clientId ?? ''}
              onChange={(event) => setClientId(event.target.value)}
              required
            />

            {isApple && (
              <Field
                label={t('platform.oauth.teamId')}
                name={`${summary.provider}-team-id`}
                value={teamId ?? summary.teamId ?? ''}
                onChange={(event) => setTeamId(event.target.value)}
                required
              />
            )}
          </div>

          {isApple ? (
            <div className="form-grid">
              <Field
                label={t('platform.oauth.keyId')}
                name={`${summary.provider}-key-id`}
                value={keyId ?? summary.keyId ?? ''}
                onChange={(event) => setKeyId(event.target.value)}
                required
              />
              <div className="field">
                <div className="settings-password-label">
                  <label className="field-label" htmlFor={`${summary.provider}-private-key`}>
                    {t('platform.oauth.privateKey')}
                  </label>
                  {!showPrivateKeyInput && (
                    <button
                      type="button"
                      className="settings-inline-link"
                      onClick={() => setIsChangingPrivateKey(true)}
                    >
                      {t('platform.oauth.changeSecret')}
                    </button>
                  )}
                </div>
                {showPrivateKeyInput ? (
                  <textarea
                    id={`${summary.provider}-private-key`}
                    className="field-input oauth-key-textarea"
                    value={privateKey}
                    onChange={(event) => setPrivateKey(event.target.value)}
                    placeholder="-----BEGIN PRIVATE KEY-----"
                    required={!summary.hasPrivateKey}
                  />
                ) : (
                  <p className="settings-password-stored">{t('platform.oauth.secretStored')}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="field">
              <div className="settings-password-label">
                <label className="field-label" htmlFor={`${summary.provider}-client-secret`}>
                  {t('platform.oauth.clientSecret')}
                </label>
                {!showSecretInput && (
                  <button
                    type="button"
                    className="settings-inline-link"
                    onClick={() => setIsChangingSecret(true)}
                  >
                    {t('platform.oauth.changeSecret')}
                  </button>
                )}
              </div>
              {showSecretInput ? (
                <input
                  id={`${summary.provider}-client-secret`}
                  type="password"
                  className="field-input"
                  value={clientSecret}
                  onChange={(event) => setClientSecret(event.target.value)}
                  autoComplete="new-password"
                  required={!summary.hasClientSecret}
                />
              ) : (
                <p className="settings-password-stored">{t('platform.oauth.secretStored')}</p>
              )}
            </div>
          )}

          {summary.updatedAt && (
            <p className="settings-status-hint">
              {t('platform.oauth.lastUpdate', {
                values: { date: formatDateTime(summary.updatedAt, locale) },
              })}
            </p>
          )}

          {saveError && <Alert tone="danger">{saveError}</Alert>}

          <div className="oauth-provider-form-actions">
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t('common.actions.save')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setExpanded(false)}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/**
 * Platform-wide OAuth provider administration.
 *
 * Not a tenant page — gated on `platform.settings.*`, same as the SMTP
 * screen. Each provider is independent: its own credentials, its own
 * switch. Flipping a provider on with no credentials saved is refused
 * client-side before it ever reaches the server, since the server would
 * only reject it anyway.
 */
export function OAuthSettingsPage(): ReactNode {
  const { t } = useI18n();

  const providers = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => platformApi.getOAuthProviderCredentials(),
  });

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('platform.oauth.title')}</h1>
          <p className="page-count">{t('platform.oauth.subtitle')}</p>
        </div>
      </header>

      {providers.isError && <Alert tone="danger">{errorMessage(providers.error, t)}</Alert>}

      <div className="oauth-provider-list">
        {providers.data?.map((summary: OAuthProviderCredentialsSummary) => (
          <ProviderCard key={summary.provider} summary={summary} />
        ))}
      </div>
    </div>
  );
}
